// /live — placeholder + (later) live broadcast page for the webinar.
//
// Right now: shows a countdown to the event and a "we're going live soon"
// notice in the same design system as the rest of the funnel. Closer to
// the event we'll swap the live-placeholder block for the actual broadcast
// embed (Zoom/StreamYard/whatever the platform is).
//
// The Google/Outlook/ICS calendar invites generated on /webinar-thank-you
// drop https://app-academy.ai/live into the event's location field, so
// registered attendees click straight here at start time.
//
// noindex by default — we don't want search engines indexing an empty
// placeholder and ranking it for "AI App Academy live".

import React from 'react';
import { Icon } from './Icon.jsx';
import { LogoMark } from './LogoMark.jsx';
import { CookieBanner, AAConsent } from './CookieBanner.jsx';
import {
  WEBINAR_DATE,
  WEBINAR_FORMAT,
  DATE_FMT,
  TIME_FMT,
} from '../lib/webinar.js';

function useCountdown(target) {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, target.getTime() - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  return { days, hours, minutes, seconds, isPast: diff === 0 };
}

function CountdownBox({ value, label }) {
  return (
    <div className="countdown-box">
      <div className="countdown-num">{String(value).padStart(2, '0')}</div>
      <div className="countdown-label">{label}</div>
    </div>
  );
}

function useLiveHead() {
  React.useEffect(() => {
    const prevTitle = document.title;
    document.title = 'AI App Academy · Live-Webinar';

    let robots = document.querySelector('meta[name="robots"]');
    const hadRobots = !!robots;
    const prevRobots = robots ? robots.getAttribute('content') : null;
    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
    }
    robots.setAttribute('content', 'noindex, nofollow');

    return () => {
      document.title = prevTitle;
      if (hadRobots && prevRobots != null) robots.setAttribute('content', prevRobots);
      else if (!hadRobots && robots) robots.remove();
    };
  }, []);
}

export default function WebinarLivePage() {
  useLiveHead();
  const { days, hours, minutes, seconds, isPast } = useCountdown(WEBINAR_DATE);

  // Auto-reload once when the countdown crosses WEBINAR_DATE. Otherwise a
  // user who left the tab open since registration would see the React state
  // flip to "Wir sind live", but a hard-coded stream embed (YouTube Live,
  // Zoom webclient, etc.) only initialises on a fresh page mount — they'd
  // still need to manually F5. A one-shot reload ~3s after isPast flips
  // gives the embed a clean boot. The ref logic guarantees we don't
  // re-trigger on the post-reload render (when isPast is already true).
  const initialLoadWasFuture = React.useRef(null);
  React.useEffect(() => {
    if (initialLoadWasFuture.current === null) {
      initialLoadWasFuture.current = !isPast;
      return;
    }
    if (initialLoadWasFuture.current && isPast) {
      initialLoadWasFuture.current = false;
      const t = setTimeout(() => window.location.reload(), 3000);
      return () => clearTimeout(t);
    }
  }, [isPast]);

  return (
    <>
      <nav className="nav nav-webinar">
        <div className="nav-inner">
          <a href="/" className="logo">
            <div className="logo-mark"><LogoMark size={32} /></div>
            <span className="logo-text">AI App Academy</span>
          </a>
        </div>
      </nav>

      <main className="hero webinar-hero live-hero" id="top">
        <div className="hero-bg-grid" aria-hidden="true"></div>
        <div className="container">
          <div className="webinar-eyebrow">
            <span className="webinar-live-dot"></span>
            <span style={{ textTransform: 'none' }}>LIVE-WEBINAR</span>
          </div>

          {isPast ? (
            <>
              <h1 className="hero-headline webinar-headline live-headline">
                Wir sind <em>live</em>.
              </h1>
              <p className="webinar-sub">
                Das Webinar läuft gerade. Falls der Stream unten noch nicht erscheint,
                lade die Seite kurz neu.
              </p>
              <div className="live-placeholder">
                <div className="live-placeholder-icon"><Icon.Sparkles size={28} /></div>
                <h3>Live-Stream wird hier eingebettet</h3>
                <p>
                  Der Broadcast wird sichtbar, sobald die Übertragung läuft.
                  Sollte das Fenster leer bleiben, ist eventuell deine
                  Verbindung kurz unterbrochen. Lade einfach kurz neu.
                </p>
                <a href="/termin" className="btn btn-primary live-termin-cta">
                  <span>1:1 Strategiegespräch sichern</span>
                  <span className="btn-arrow"><Icon.ArrowRight size={14} /></span>
                </a>
                <span className="live-placeholder-note">
                  Direkt im Anschluss an das Webinar mit Dominik.
                </span>
              </div>
            </>
          ) : (
            <>
              <h1 className="hero-headline webinar-headline live-headline">
                Wir gehen <em>bald</em> live.
              </h1>
              <p className="webinar-sub">
                Das Webinar startet am <strong>{DATE_FMT.format(WEBINAR_DATE)}</strong> um{' '}
                <strong>{TIME_FMT.format(WEBINAR_DATE)} Uhr</strong>. Lass diese Seite
                offen oder komm ein paar Minuten vor Beginn zurück. Der Live-Stream
                wird hier dann automatisch eingebunden.
              </p>

              <div className="event-meta-pill">
                <span className="event-meta-item">
                  <Icon.Calendar size={16} />
                  <span>{DATE_FMT.format(WEBINAR_DATE)}</span>
                </span>
                <span className="event-meta-divider"></span>
                <span className="event-meta-item">
                  <Icon.Clock size={16} />
                  <span>{TIME_FMT.format(WEBINAR_DATE)} Uhr</span>
                </span>
                <span className="event-meta-divider"></span>
                <span className="event-meta-item">
                  <span>{WEBINAR_FORMAT}</span>
                </span>
              </div>

              <div className="countdown" aria-label="Countdown bis zum Webinar">
                <CountdownBox value={days} label="Tage" />
                <span className="countdown-sep">:</span>
                <CountdownBox value={hours} label="Stunden" />
                <span className="countdown-sep">:</span>
                <CountdownBox value={minutes} label="Minuten" />
                <span className="countdown-sep">:</span>
                <CountdownBox value={seconds} label="Sekunden" />
              </div>

              <div className="live-placeholder">
                <div className="live-placeholder-icon"><Icon.Lock size={26} /></div>
                <h3>Diese Seite wird kurz vor dem Webinar aktiv</h3>
                <p>
                  Der Live-Stream startet erst zur Webinar-Zeit. Du musst nichts weiter
                  tun. Sobald wir auf Sendung gehen, siehst du den Stream genau hier.
                </p>
                <a href="/webinar" className="live-placeholder-link">
                  <span>Noch nicht angemeldet? Hier geht's zur Anmeldung</span>
                  <Icon.ArrowRight size={14} />
                </a>
              </div>
            </>
          )}
        </div>
      </main>

      <footer className="webinar-footer">
        <div className="container">
          <div className="webinar-footer-row">
            <div className="webinar-footer-brand">
              <div className="logo-mark"><LogoMark size={24} /></div>
              <span>© 2026 Incibit GmbH · App Academy</span>
            </div>
            <div className="webinar-footer-links">
              <a href="https://incibit.dev/impressum/" target="_blank" rel="noreferrer">Impressum</a>
              <a href="https://incibit.dev/privacy-policy/" target="_blank" rel="noreferrer">Datenschutz</a>
              <a href="https://incibit.dev/agb/" target="_blank" rel="noreferrer">AGB</a>
              <a
                href="#cookie-settings"
                onClick={(e) => { e.preventDefault(); AAConsent.openSettings(); }}
              >
                Cookie-Einstellungen
              </a>
            </div>
          </div>
        </div>
      </footer>

      <CookieBanner />
    </>
  );
}
